import { cn } from '@/lib/utils';
import { Calendar } from 'lucide-react';
import { ChantierIcon } from '@/components/chantiers/ChantierIcon';

interface ChantierCardProps {
  chantier: {
    id: string;
    nom_chantier: string;
    client: string;
    rentabilite: number;
    categorie?: string;
    date_debut_prevue?: string;
    date_fin_estimee?: string;
    pictogramme?: string;
    couleur?: string;
  };
  isSelected: boolean;
  onClick: () => void;
  rentabiliteLabel?: string; // Libellé personnalisable (par défaut: "Marge actuelle")
}

// Formater une date en format court (ex: 15 jan)
const formatDateShort = (dateStr?: string) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Icônes par catégorie de chantier
const CATEGORY_ICONS: Record<string, string> = {
  gros_oeuvre: '🏗️',
  toiture: '🏠',
  salle_de_bain: '🛁',
  cuisine: '🍳',
  electricite: '⚡',
  plomberie: '🚰',
  peinture: '🎨',
  menuiserie: '🪟',
  chauffage_clim: '🌡️',
  exterieur: '🏢',
  renovation: '🔧',
  autre: '📋',
};

export function ChantierCard({ chantier, isSelected, onClick, rentabiliteLabel = "Marge actuelle" }: ChantierCardProps) {
  const rentabilite = chantier.rentabilite || 0;

  // Couleur selon la rentabilité
  const getRentabiliteColor = (value: number) => {
    if (value < 0) return { bar: 'bg-red-500', text: 'text-red-400' };
    if (value < 10) return { bar: 'bg-orange-500', text: 'text-orange-400' };
    if (value < 30) return { bar: 'bg-yellow-500', text: 'text-yellow-400' };
    return { bar: 'bg-green-500', text: 'text-green-400' };
  };

  const colors = getRentabiliteColor(rentabilite);

  // Calculer la largeur de la barre (entre 0 et 100%)
  const barWidth = Math.min(Math.max(rentabilite, 0), 100);

  // Utiliser les nouvelles valeurs pictogramme/couleur ou fallback vers categorie
  const hasPictogramme = chantier.pictogramme && chantier.couleur;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2.5 rounded-xl border transition-all duration-150',
        'hover:shadow-md',
        isSelected
          ? 'bg-primary/10 border-primary shadow-sm'
          : 'bg-card/50 border-border hover:bg-muted/50 hover:border-muted-foreground/30'
      )}
    >
      {/* Ligne 1 : Icône + Nom + Indicateur sélection */}
      <div className="flex items-center gap-2 mb-1">
        {hasPictogramme ? (
          <ChantierIcon
            pictogramme={chantier.pictogramme!}
            couleur={chantier.couleur!}
            size="sm"
            showBackground={true}
          />
        ) : (
          <span className="text-base">{CATEGORY_ICONS[chantier.categorie || 'renovation'] || '🏗️'}</span>
        )}
        <span className={cn(
          'font-medium text-sm truncate flex-1',
          isSelected && 'text-primary'
        )}>
          {chantier.nom_chantier}
        </span>
        {isSelected && (
          <span className="text-primary text-xs">◀</span>
        )}
      </div>

      {/* Ligne 2 : Client */}
      <div className="text-xs text-foreground/80 truncate mb-1 pl-6">
        {chantier.client}
      </div>

      {/* Ligne 3 : Dates */}
      {(chantier.date_debut_prevue || chantier.date_fin_estimee) && (
        <div className="flex items-center gap-1 text-xs text-foreground/70 mb-2 pl-6">
          <Calendar className="w-3 h-3" />
          <span>
            {formatDateShort(chantier.date_debut_prevue) || '?'}
            {' → '}
            {formatDateShort(chantier.date_fin_estimee) || '?'}
          </span>
        </div>
      )}

      {/* Ligne 4 : Barre de rentabilité + pourcentage */}
      <div className="pl-6 space-y-1">
        <div className="flex items-center gap-2">
          {/* Barre de progression */}
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-300', colors.bar)}
              style={{ width: `${barWidth}%` }}
            />
          </div>

          {/* Pourcentage */}
          <span className={cn('text-xs font-medium tabular-nums min-w-[40px] text-right', colors.text)}>
            {rentabilite.toFixed(1)}%
          </span>
        </div>
        {/* Libellé */}
        <p className="text-[10px] text-foreground/60">{rentabiliteLabel}</p>
      </div>
    </button>
  );
}
